<template>
  <v-dialog max-width="900px" persistent v-model="dialog" scrollable>
    <v-card class="new-customer-card">
      <!-- Modern Header -->
      <div class="new-customer-header">
        <div class="header-icon">
          <v-icon color="#fddf00" size="32">person_add</v-icon>
        </div>
        <div class="header-content">
          <h2 class="header-title">Create New Customer</h2>
          <p class="header-subtitle">Add a new customer account with address</p>
        </div>
        <v-btn
          icon
          :disabled="adding_customer"
          @click="$emit('setDialog', false)"
          class="close-btn"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </div>

      <v-divider></v-divider>

      <!-- Form Content -->
      <v-card-text class="new-customer-content">
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
          class="customer-form"
        >
          <!-- Personal Information Section -->
          <div class="form-section">
            <div class="section-header">
              <h4 class="section-title">
                <v-icon color="#fddf00" class="mr-2">person</v-icon>
                Personal Information
              </h4>
            </div>

            <div class="form-grid">
              <v-text-field
                v-model="fname"
                label="First Name"
                outlined
                color="#fddf00"
                :rules="[nameValid.max, nameValid.required]"
                class="form-field"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">badge</v-icon>
                </template>
              </v-text-field>

              <v-text-field
                v-model="lname"
                label="Last Name"
                outlined
                color="#fddf00"
                :rules="[nameValid.max, nameValid.required]"
                class="form-field"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">badge</v-icon>
                </template>
              </v-text-field>
            </div>

            <v-text-field
              v-model="email"
              label="Email Address"
              outlined
              color="#fddf00"
              :rules="[
                emailValid.email,
                emailValid.required,
                emailValid.already,
              ]"
              class="form-field"
              placeholder="Enter email address"
            >
              <template v-slot:prepend-inner>
                <v-icon color="#fddf00" size="20">email</v-icon>
              </template>
            </v-text-field>

            <div class="phone-group">
              <v-select
                :items="country_tel"
                v-model="user_country_tel"
                label="Country Code"
                outlined
                color="#fddf00"
                class="country-select"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">public</v-icon>
                </template>
              </v-select>

              <v-text-field
                v-model="tel"
                label="Phone Number"
                outlined
                color="#fddf00"
                :rules="[telValid.leng, telValid.already, telValid.required]"
                class="phone-input"
                placeholder="Enter phone number"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">phone</v-icon>
                </template>
              </v-text-field>
            </div>
          </div>

          <!-- Password Section -->
          <div class="form-section">
            <div class="section-header">
              <h4 class="section-title">
                <v-icon color="#fddf00" class="mr-2">lock</v-icon>
                Account Security
              </h4>
            </div>

            <div class="password-grid">
              <v-text-field
                v-model="password1"
                :type="show1 ? 'text' : 'password'"
                label="Password"
                outlined
                color="#fddf00"
                :rules="[passwordValid.min, passwordValid.max]"
                class="form-field"
                hint="At least 6 characters"
                persistent-hint
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">lock</v-icon>
                </template>
                <template v-slot:append>
                  <v-btn icon @click="show1 = !show1" class="password-toggle">
                    <v-icon>{{
                      show1 ? "visibility_off" : "visibility"
                    }}</v-icon>
                  </v-btn>
                </template>
              </v-text-field>

              <v-text-field
                v-model="password2"
                :type="show2 ? 'text' : 'password'"
                label="Confirm Password"
                outlined
                color="#fddf00"
                :rules="[passwordValid.min, passwordValid.max]"
                class="form-field"
                hint="Re-enter password"
                persistent-hint
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">lock_outline</v-icon>
                </template>
                <template v-slot:append>
                  <v-btn icon @click="show2 = !show2" class="password-toggle">
                    <v-icon>{{
                      show2 ? "visibility_off" : "visibility"
                    }}</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </div>

            <!-- Password Match Indicator -->
            <div v-if="password1 && password2" class="password-match-indicator">
              <div
                class="match-status"
                :class="{
                  'match-success': passwordMatch,
                  'match-error': !passwordMatch,
                }"
              >
                <v-icon :color="passwordMatch ? 'success' : 'error'" size="16">
                  {{ passwordMatch ? "check_circle" : "error" }}
                </v-icon>
                <span>{{
                  passwordMatch ? "Passwords match" : "Passwords do not match"
                }}</span>
              </div>
            </div>
          </div>

          <!-- Address Section -->
          <div class="form-section">
            <div class="section-header">
              <h4 class="section-title">
                <v-icon color="#fddf00" class="mr-2">location_on</v-icon>
                Address Information
              </h4>
            </div>

            <v-textarea
              v-model="customer_address"
              label="Street Address"
              outlined
              color="#fddf00"
              rows="2"
              :rules="[rules.required]"
              class="form-field"
              placeholder="Enter complete address"
            >
              <template v-slot:prepend-inner>
                <v-icon color="#fddf00" size="20">home</v-icon>
              </template>
            </v-textarea>

            <div class="location-grid">
              <v-select
                :items="provinceItems"
                v-model="customer_province"
                @change="onChangeProvince"
                item-text="province_name_th"
                item-value="province_id"
                label="Province"
                outlined
                color="#fddf00"
                :rules="[rules.required]"
                class="form-field"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">map</v-icon>
                </template>
              </v-select>

              <v-select
                :items="districtItems"
                v-model="customer_district"
                @change="onChangeDistrict"
                item-text="district_name_th"
                item-value="district_id"
                label="District"
                outlined
                color="#fddf00"
                :rules="[rules.required]"
                class="form-field"
              ></v-select>

              <v-select
                :items="subDistrictItems"
                v-model="customer_sub_district"
                item-text="sub_district_name_th"
                item-value="sub_district_id"
                label="Sub District"
                outlined
                color="#fddf00"
                :rules="[rules.required]"
                class="form-field"
              ></v-select>

              <v-select
                :items="postcodeItems"
                v-model="customer_post_code"
                item-text="zipcode_code"
                item-value="zipcode_id"
                label="Postal Code"
                outlined
                color="#fddf00"
                :rules="[rules.required]"
                class="form-field"
              ></v-select>
            </div>

            <v-textarea
              v-model="customer_place_note"
              label="Address Notes"
              outlined
              color="#fddf00"
              rows="2"
              class="form-field"
              placeholder="Delivery instructions, landmarks, etc. (Optional)"
            >
              <template v-slot:prepend-inner>
                <v-icon color="#fddf00" size="20">note</v-icon>
              </template>
            </v-textarea>

            <!-- Coordinates Section -->
            <div class="coordinates-section">
              <h5 class="subsection-title">Location Coordinates</h5>

              <div class="coordinates-grid">
                <v-text-field
                  v-model="customer_address_lat"
                  label="Latitude"
                  outlined
                  color="#fddf00"
                  :rules="[rules.required, rules.onlyDouble]"
                  class="form-field"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="#fddf00" size="20">my_location</v-icon>
                  </template>
                </v-text-field>

                <v-text-field
                  v-model="customer_address_long"
                  label="Longitude"
                  outlined
                  color="#fddf00"
                  :rules="[rules.required, rules.onlyDouble]"
                  class="form-field"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="#fddf00" size="20">location_on</v-icon>
                  </template>
                </v-text-field>
              </div>

              <v-btn
                @click="change_location++"
                color="#fddf00"
                outlined
                class="map-update-btn"
              >
                <v-icon class="mr-2">refresh</v-icon>
                Update Map View
              </v-btn>

              <!-- Map Container -->
              <div class="map-container">
                <GoogleMap
                  :latitude="parseFloat(customer_address_lat) || 13.7563"
                  :longitude="parseFloat(customer_address_long) || 100.5018"
                  :change="change_location"
                  :height="300"
                  :zoom="15"
                  :mapType="'all'"
                  :draggable="true"
                  @draged="getDragLocation($event)"
                />
              </div>
            </div>
          </div>
        </v-form>
      </v-card-text>

      <!-- Action Buttons -->
      <v-card-actions class="new-customer-actions">
        <v-btn
          @click="clear()"
          :disabled="adding_customer"
          color="grey"
          outlined
          class="action-btn clear-form-btn"
        >
          <v-icon class="mr-2" color="white">clear</v-icon>
          <span class="clear-form-text">Clear Form</span>
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn
          @click="add()"
          :disabled="adding_customer || !valid || !passwordMatch"
          :loading="adding_customer"
          color="#fddf00"
          class="action-btn save-btn"
        >
          <v-icon class="mr-2">person_add</v-icon>
          Create Customer
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";

export default {
  props: ["dialog"],
  data() {
    return {
      adding_customer: false,
      change_location: 0,
      passwordMatch: true,
      user_country_tel: "+66",
      tel: "",
      country_tel: ["+66"],
      password1: "",
      password2: "",
      show1: false,
      show2: false,
      email: "",
      fname: "",
      lname: "",
      valid: true,
      customer_address_lat: 13.7563, // Default to Bangkok
      customer_address_long: 100.5018,
      customer_address: "",
      customer_province: "",
      customer_district: "",
      customer_sub_district: "",
      customer_post_code: "",
      customer_place_note: "",

      // Validation rules
      rules: {
        onlyDouble: (value) => {
          const pattern = /^-?([0-9]?[0-9]?[0-9]?[0-9]|[0-9]0)\.{1}\d{1,100}$/;
          return pattern.test(value) || "Invalid coordinate format";
        },
        required: (value) => !!value || "This field is required",
      },
      telValid: {
        required: (value) => !!value || "Phone number is required",
        leng: (v) => v.length == 9 || "Phone number must be 9 digits",
        already: (a) => !this.telAlready(a) || "Phone number already in use",
      },
      nameValid: {
        required: (value) => !!value || "Name is required",
        max: (v) => v.length <= 55 || "Maximum 55 characters allowed",
      },
      emailValid: {
        already: (a) => !this.emailAlready(a) || "Email already in use",
        required: (value) => !!value || "Email is required",
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid email address";
        },
      },
      passwordValid: {
        min: (v) => v.length >= 6 || "At least 6 characters required",
        max: (v) => v.length <= 24 || "Maximum 24 characters allowed",
      },
    };
  },

  watch: {
    password1() {
      this.checkPasswordMatch();
    },
    password2() {
      this.checkPasswordMatch();
    },
  },

  computed: {
    ...mapState({
      customerData: (state) => state.fetchCustomerBack.customerData,
      provinceItems: (state) => state.fetchCustomerBack.provinceAdd,
      districtItems: (state) => state.fetchCustomerBack.districtAdd,
      subDistrictItems: (state) => state.fetchCustomerBack.subDistrictAdd,
      postcodeItems: (state) => state.fetchCustomerBack.zipcodeAdd,
    }),
  },

  methods: {
    checkPasswordMatch() {
      if (this.password1.length > 5 && this.password2.length > 5) {
        this.passwordMatch = this.password1 === this.password2;
      } else {
        this.passwordMatch = true; // Don't show error if passwords are too short
      }
    },

    getDragLocation(val) {
      this.customer_address_lat = val.lat;
      this.customer_address_long = val.lng;
    },

    onChangeDistrict() {
      this.customer_sub_district = "";
      this.customer_post_code = "";
      this.$store.dispatch(
        "fetchCustomerBack/getSubDistrictAdd",
        this.customer_district
      );
      this.$store.dispatch(
        "fetchCustomerBack/getZipCodeAdd",
        this.customer_district
      );
    },

    onChangeProvince() {
      this.customer_sub_district = "";
      this.customer_district = "";
      this.customer_post_code = "";
      this.$store.dispatch(
        "fetchCustomerBack/getDistrictAdd",
        this.customer_province
      );
    },

    telAlready(a) {
      return this.customerData.some((customer) => customer.user_tel === a);
    },

    emailAlready(a) {
      return this.customerData.some((customer) => customer.user_email === a);
    },

    async add() {
      if (this.$refs.form.validate() && this.passwordMatch) {
        this.adding_customer = true;

        try {
          const userInfo = {
            user_country_tel: this.user_country_tel,
            user_tel: this.tel,
            user_type: 1,
            user_email: this.email,
            user_password: this.password2,
            user_first_name: this.fname,
            user_last_name: this.lname,
            user_role: 6,
          };

          const checkSuccess = await this.$store.dispatch(
            "fetchCustomerBack.addCustomer",
            userInfo
          );

          if (checkSuccess !== "error") {
            const addressData = {
              user_address_default: 1,
              user_address_text: this.customer_address,
              user_address_lat: parseFloat(this.customer_address_lat),
              user_address_long: parseFloat(this.customer_address_long),
              user_address_sub_district: this.customer_sub_district,
              user_address_district: this.customer_district,
              user_address_province: this.customer_province,
              user_address_zipcode: this.customer_post_code,
              user_address_user: checkSuccess,
              user_address_note: this.customer_place_note,
            };

            await this.$store.dispatch(
              "fetchCustomerBack/addCustomerAddress",
              addressData
            );
            this.$emit("setDialog", false);
            this.clear();
          } else {
            alert("Unable to create customer account");
          }
        } catch (error) {
          console.error("Error creating customer:", error);
          alert("An error occurred while creating the customer");
        } finally {
          this.adding_customer = false;
        }
      }
    },

    clear() {
      // Reset all form fields
      this.fname = "";
      this.lname = "";
      this.email = "";
      this.tel = "";
      this.password1 = "";
      this.password2 = "";
      this.customer_address = "";
      this.customer_province = "";
      this.customer_district = "";
      this.customer_sub_district = "";
      this.customer_post_code = "";
      this.customer_place_note = "";
      this.customer_address_lat = 13.7563;
      this.customer_address_long = 100.5018;
      this.change_location++;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style scoped>
.new-customer-card {
  border-radius: 16px;
  overflow: hidden;
}

.new-customer-header {
  display: flex;
  align-items: center;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #fddf00 0%, #f39c12 100%);
  gap: 1rem;
}

.header-icon {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-content {
  flex: 1;
}

.header-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: black;
  margin: 0;
}

.header-subtitle {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.8);
  margin: 0.25rem 0 0 0;
}

.close-btn {
  background: rgba(0, 0, 0, 0.1) !important;
  color: black !important;
}

.new-customer-content {
  padding: 2rem;
  max-height: 70vh;
  overflow-y: auto;
}

.customer-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-field {
  margin-bottom: 1rem;
}

.phone-group {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 1rem;
  align-items: start;
}

.password-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.password-toggle {
  color: #6b7280 !important;
}

.password-match-indicator {
  margin-top: 0.5rem;
}

.match-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  padding: 0.5rem;
  border-radius: 6px;
}

.match-success {
  background: rgba(76, 175, 80, 0.1);
  color: #4caf50;
}

.match-error {
  background: rgba(244, 67, 54, 0.1);
  color: #f44336;
}

.location-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.coordinates-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.subsection-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #4b5563;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.subsection-title::before {
  content: "";
  width: 3px;
  height: 16px;
  background: #fddf00;
  border-radius: 2px;
}

.coordinates-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.map-update-btn {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  margin-bottom: 1rem;
}

.map-container {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.new-customer-actions {
  padding: 1rem 2rem 1.5rem;
  background: #f8f9fa;
  border-top: 1px solid #e5e7eb;
  gap: 1rem;
}

.action-btn {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  min-width: 120px;
}

.save-btn {
  color: black !important;
  min-width: 160px;
}

.save-btn:disabled {
  opacity: 0.6 !important;
}

.clear-form-btn {
  border-color: #9e9e9e !important;
}

.clear-form-text {
  color: white !important;
}

/* Scrollbar Styling */
.new-customer-content::-webkit-scrollbar {
  width: 6px;
}

.new-customer-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.new-customer-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.new-customer-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .new-customer-header {
    padding: 1rem 1.5rem;
  }

  .new-customer-content {
    padding: 1.5rem;
  }

  .form-section {
    padding: 1rem;
  }

  .form-grid,
  .password-grid,
  .location-grid,
  .coordinates-grid {
    grid-template-columns: 1fr;
  }

  .phone-group {
    grid-template-columns: 1fr;
  }

  .new-customer-actions {
    padding: 1rem 1.5rem;
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
    min-width: auto;
  }
}

@media (max-width: 480px) {
  .new-customer-content {
    padding: 1rem;
  }

  .form-section {
    padding: 0.75rem;
  }
}
</style>

